import { useState, useCallback, useRef, useEffect, useMemo, type CSSProperties } from "react";
import { AppRoute } from "../types/routes";
import { useNavigation } from "./useNavigation";
import { useSignupApi } from "../api/use-signup-api";
import { isNotEmpty } from "../utils/type-utils";
import { useAuthApi } from "../api/useAuthApi";
import { AuthUser } from "../types/auth-types";

import { trackEvent } from "../utils/firebase-analytics";
import { completeSignup, GOOGLE_SIGNUP_KEY } from "../utils/signup-analytics";

type ToastState = {
  message: string;
  type: "success" | "error" | "info" | "warning";
  isVisible: boolean;
};

const INITIAL_OTP = ["", "", "", "", "", ""];

const useSignup = (onAuthSuccess?: (user: AuthUser) => void) => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { replace } = useNavigation();
  const { signup, verifyOtp, resendOtp } = useSignupApi();
  const { fetchAccount, handleGoogleSignIn: handleGoogleSignInApi } = useAuthApi();

  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState(INITIAL_OTP);
  const [resendCountdown, setResendCountdown] = useState(30);
  const [resendLoading, setResendLoading] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const showToast = useCallback((message: string, type: ToastState["type"]) => {
    setToast({ message, type, isVisible: true });
  }, []);

  useEffect(() => {
    if (!showOtp || resendCountdown <= 0) return;

    const timer = setTimeout(() => {
      setResendCountdown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearTimeout(timer);
  }, [showOtp, resendCountdown]);

  const handleSignup = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (loading) return;
      trackEvent("signup_started", { method: "email" });
      setLoading(true);
      try {
        const response = await signup(email, password);
        if (response.status === "success") {
          showToast(
            response.message || "Signup successful. OTP sent to your email.",
            "success"
          );
          trackEvent("verification_sent", { method: "email", reason: "initial" });
          setShowOtp(true);
          setResendCountdown(30);
        } else {
          trackEvent("signup_failed", { method: "email", stage: "signup_request" });
          showToast(response.message || "Signup failed. Please try again.", "error");
        }
      } catch (error: any) {
        trackEvent("signup_failed", { method: "email", stage: "signup_request" });
        showToast(
          error?.response?.data?.detail ||
            error?.response?.data?.message ||
            "Signup failed. Please try again.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    },
    [email, password, showToast, signup, loading]
  );

  const handleGoogleSignup = useCallback(async () => {
    if (loading) return;
    trackEvent("signup_started", { method: "google" });
    try { sessionStorage.setItem(GOOGLE_SIGNUP_KEY, String(Date.now())); } catch {}
    setLoading(true);
    try {
      const redirecting = await handleGoogleSignInApi();
      if (!redirecting) {
        trackEvent("signup_failed", { method: "google", stage: "oauth_start" });
        try { sessionStorage.removeItem(GOOGLE_SIGNUP_KEY); } catch {}
        showToast("Google sign in failed. Please try again.", "error");
      }
    } catch (error) {
      trackEvent("signup_failed", { method: "google", stage: "oauth_start" });
      try { sessionStorage.removeItem(GOOGLE_SIGNUP_KEY); } catch {}
      console.error("Google signup failed", error);
      showToast("Google sign in failed. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  }, [handleGoogleSignInApi, showToast, loading]);

  const navigateToLogin = useCallback(() => {
    replace(AppRoute.LOGIN);
  }, [replace]);

  const handleOtpChange = useCallback((index: number, value: string) => {
    if (isNaN(Number(value))) return;

    setOtp((prevOtp) => {
      const newOtp = [...prevOtp];
      newOtp[index] = value;
      return newOtp;
    });

    if (value !== "" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  }, []);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>, startIndex: number) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData("text");
      const digits = pastedData.replace(/\D/g, "").split("");

      if (digits.length === 0) return;

      setOtp((prev) => {
        const newOtp = [...prev];
        digits.forEach((digit, i) => {
          const targetIndex = startIndex + i;
          if (targetIndex < 6) {
            newOtp[targetIndex] = digit;
          }
        });
        return newOtp;
      });

      const lastFilledIndex = Math.min(startIndex + digits.length - 1, 5);
      inputRefs.current[lastFilledIndex]?.focus();
    },
    []
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && index > 0 && e.currentTarget.value === "") {
        inputRefs.current[index - 1]?.focus();
      }
    },
    []
  );

  const verificationComplete = useRef(false);
  const handleVerifyEmail = useCallback(async () => {
    if (loading || verificationComplete.current) return;
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      trackEvent("verification_failed", { method: "email", stage: "incomplete_code" });
      showToast("Please enter the 6-digit code.", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await verifyOtp(email, otpString);
      if (isNotEmpty(response) && response.status === "success" && response.user?.id) {
        verificationComplete.current = true;
        trackEvent("verification_completed", { method: "email" });
        completeSignup(response.user.id, "email");
        showToast("Email verified successfully.", "success");
        await fetchAccount();
        if (isNotEmpty(onAuthSuccess)) onAuthSuccess(response.user as AuthUser);
      } else {
        trackEvent("verification_failed", { method: "email", stage: "verify_request" });
        showToast((response as any)?.message || "Invalid OTP. Please try again.", "error");
      }
    } catch {
      trackEvent("verification_failed", { method: "email", stage: "verify_request" });
      showToast("Email verification failed. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  }, [otp, showToast, verifyOtp, email, fetchAccount, onAuthSuccess, loading]);

  const handleResendOtp = useCallback(async () => {
    if (resendCountdown > 0 || resendLoading) return;

    setResendLoading(true);
    try {
      const result = await resendOtp(email);

      if (result.ok) {
        trackEvent("verification_sent", { method: "email", reason: "resend" });
        setResendCountdown(30);
        showToast(result.message || "Verification code resent.", "success");
      } else {
        trackEvent("verification_failed", { method: "email", stage: "resend_request" });
        showToast(result.message || "Failed to resend code.", "error");
      }
    } catch {
      trackEvent("verification_failed", { method: "email", stage: "resend_request" });
      showToast("Failed to resend code. Please try again.", "error");
    } finally {
      setResendLoading(false);
    }
  }, [email, resendCountdown, resendLoading, resendOtp, showToast]);

  const handleToastClose = useCallback(() => {
    setToast(null);
  }, []);

  const handleBackToSignup = useCallback(() => {
    setShowOtp(false);
    setOtp(INITIAL_OTP);
  }, []);

  const handleOtpInputRef = useCallback((index: number, element: HTMLInputElement | null) => {
    inputRefs.current[index] = element;
  }, []);

  const handleEmailChange = useCallback((value: string) => {
    setEmail(value);
  }, []);

  const handlePasswordChange = useCallback((value: string) => {
    setPassword(value);
  }, []);

  const passwordStrength = useMemo(() => {
    const weak = password.length > 0;
    const medium = password.length > 6;
    const good = password.length > 8;
    return {
      weak,
      medium,
      good,
      label: good ? "Good" : weak ? "Weak" : "",
    };
  }, [password]);

  const resendState = useMemo(() => {
    const disabled = resendCountdown > 0 || resendLoading;
    const label = resendLoading
      ? "Resending..."
      : resendCountdown > 0
      ? `Resend code (${resendCountdown}s)`
      : "Resend code";

    return {
      disabled,
      label,
      style: {
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      } as CSSProperties,
    };
  }, [resendCountdown, resendLoading]);

  return {
    setFullName,
    handleVerifyEmail,
    handleBackToSignup,
    handleKeyDown,
    handlePaste,
    setEmail,
    setPassword,
    handleSignup,
    handleGoogleSignup,
    fullName,
    handleOtpChange,
    email,
    navigateToLogin,
    password,
    loading,
    showOtp,
    setShowOtp,
    otp,
    setOtp,
    inputRefs,
    resendCountdown,
    resendLoading,
    handleResendOtp,
    toast,
    handleToastClose,
    handleOtpInputRef,
    handleEmailChange,
    handlePasswordChange,
    passwordStrength,
    resendState,
  };
};

export default useSignup;
